import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import { Modal } from '../ui/modal';
import Button from '../ui/button/Button';

interface ShippingServiceModalProps {
    service?: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function ShippingServiceModal({
    service,
    isOpen,
    onClose,
    onSave
}: ShippingServiceModalProps) {
    const [form, setForm] = useState({
        name: '',
        slug: '',
        label: '',
        description: '',
        active: true
    });

    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (service) {
            setForm({
                name: service.name || '',
                slug: service.slug || '',
                label: service.label || '',
                description: service.description || '',
                active: !!service.active
            });
        } else {
            setForm({
                name: '',
                slug: '',
                label: '',
                description: '',
                active: true
            });
        }
    }, [service, isOpen]);

    const handleSave = async () => {
        if (!form.name.trim()) {
            await Swal.fire({
                title: 'Validation Error',
                text: 'Name is required',
                icon: 'warning',
                confirmButtonColor: '#3085d6'
            });
            return;
        }
        if (!form.label.trim()) {
            await Swal.fire({
                title: 'Validation Error',
                text: 'Label is required',
                icon: 'warning'
            });
            return;
        }


        const confirmResult = await Swal.fire({
            title: service ? 'Update Service?' : 'Create Service?',
            text: service
                ? 'Confirm update?'
                : 'Confirm create?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: service ? 'Update' : 'Create',
            cancelButtonText: 'Cancel',
            width: 350, // smaller width
            padding: '1.2em',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        });

        if (!confirmResult.isConfirmed) return;

        setIsSaving(true);

        try {
            if (service?.id) {
                await client.postApiShippingShippingServiceUpdate({
                    postApiShippingShippingServiceUpdateRequest: {
                        id: service.id,
                        ...form
                    }
                });


                await Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Shipping service updated successfully.',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
            } else {
                await client.postApiShippingShippingServiceCreate({
                    postApiShippingShippingServiceCreateRequest: form
                });



                await Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Shipping service created successfully.',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
            }

            onSave();
            onClose();
        } catch (err: any) {
            console.error('Failed to save service:', err);

            await Swal.fire({
                title: 'Error',
                text:
                    err.response?.data?.message ||
                    err.message ||
                    'Failed to save shipping service',
                icon: 'error',
                confirmButtonColor: '#d33'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-lg">
            <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden max-h-[90vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {service ? 'Edit Shipping Service' : 'Add Shipping Service'}
                    </h3>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4 overflow-y-auto flex-1">


                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="e.g. Standard Delivery"
                        />
                    </div>


                    {/* Slug */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                            Slug
                        </label>
                        <input
                            type="text"
                            value={form.slug}
                            onChange={e => setForm({ ...form, slug: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="standard-delivery"
                        />
                    </div>

                    {/* Label */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                            Label <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.label}
                            onChange={e => setForm({ ...form, label: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Shown at checkout"
                        />
                    </div>


                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            rows={3}
                        />
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                            Status
                        </label>
                        <select
                            value={form.active ? 'true' : 'false'}
                            onChange={e =>
                                setForm({ ...form, active: e.target.value === 'true' })
                            }
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
